import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ChevronDownIcon, DownloadIcon, PlusIcon, PrinterIcon } from 'lucide-react'
import { useState } from 'react'

import { LoadingRows } from '@/components/skeletons'
import { Page } from '@/components/page'
import { Segmented } from '@/components/segmented'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { PrintLabelDialog } from '@/features/labels/PrintDialog'
import { PartsFilterBar } from '@/features/parts/PartsFilterBar'
import { PartsGallery } from '@/features/parts/PartsGallery'
import { PartsTable } from '@/features/parts/PartsTable'
import { useParts, type PartsFilter } from '@/features/parts/api'
import { countActive } from '@/features/parts/filters'
import { useSettings } from '@/features/settings/api'

/**
 * The URL shape. Deliberately not the same type as `PartsFilter`: TanStack
 * Router serialises whatever `validateSearch` returns, so anything that means
 * "the default" has to be `undefined` here or it ends up spelled out in every
 * link. Tags stay a comma-joined string for the same reason — an array would be
 * JSON-encoded into the query.
 */
export interface PartsSearch {
  q?: string
  category_id?: string
  include_subcategories?: false
  footprint_id?: string
  manufacturer_id?: string
  supplier_id?: string
  location_id?: string
  tags?: string
  in_stock?: true
  low_stock?: true
  favorite?: true
  needs_review?: true
  status?: string
  sort?: string
  order?: 'desc'
  page?: number
  per_page?: number
  /** Pictures instead of rows; the table is the default and stays out of the URL. */
  view?: 'gallery'
}

const DEFAULT_PER_PAGE = 50

function parseSearch(search: Record<string, unknown>): PartsSearch {
  // The router parses ?q=470 into a number; every text parameter is text, so
  // take numbers back as their spelling instead of dropping them.
  const text = (key: string) => {
    const value = search[key]
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
    return typeof value === 'string' && value ? value : undefined
  }
  const flag = (key: string) =>
    search[key] === true || search[key] === 'true' ? (true as const) : undefined
  const count = (key: string, fallback: number) => {
    const value = Number(search[key])
    return Number.isFinite(value) && value > 0 && value !== fallback ? value : undefined
  }

  const rawTags = search.tags
  const tags = Array.isArray(rawTags)
    ? (rawTags as string[]).join(',')
    : typeof rawTags === 'string' && rawTags
      ? rawTags
      : undefined

  return {
    q: text('q'),
    category_id: text('category_id'),
    include_subcategories:
      search.include_subcategories === false || search.include_subcategories === 'false'
        ? (false as const)
        : undefined,
    footprint_id: text('footprint_id'),
    manufacturer_id: text('manufacturer_id'),
    supplier_id: text('supplier_id'),
    location_id: text('location_id'),
    tags,
    in_stock: flag('in_stock'),
    low_stock: flag('low_stock'),
    favorite: flag('favorite'),
    needs_review: flag('needs_review'),
    status: text('status'),
    sort: text('sort'),
    order: search.order === 'desc' ? ('desc' as const) : undefined,
    page: count('page', 1),
    per_page: count('per_page', DEFAULT_PER_PAGE),
    view: search.view === 'gallery' ? ('gallery' as const) : undefined,
  }
}

/** The URL shape, expanded into what the API and the filter bar want. */
function toFilter({ view: _view, ...search }: PartsSearch): PartsFilter {
  return {
    ...search,
    tags: search.tags ? search.tags.split(',').filter(Boolean) : undefined,
    page: search.page ?? 1,
    per_page: search.per_page ?? DEFAULT_PER_PAGE,
  }
}

/** The filter bar's edits, folded back into the URL shape. */
function toSearch(previous: PartsSearch, patch: Partial<PartsFilter>): PartsSearch {
  const merged: Record<string, unknown> = { ...previous, ...patch }
  if (Array.isArray(patch.tags)) {
    merged.tags = patch.tags.length ? patch.tags.join(',') : undefined
  } else if ('tags' in patch) {
    merged.tags = undefined
  }
  return parseSearch(merged)
}

export const Route = createFileRoute('/parts/')({
  validateSearch: parseSearch,
  component: PartsPage,
})

function PartsPage() {
  const search = Route.useSearch()
  const filter = toFilter(search)
  const navigate = useNavigate({ from: Route.fullPath })
  const parts = useParts(filter)
  const settings = useSettings()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [printing, setPrinting] = useState(false)

  const patch = (changes: Partial<PartsFilter>) => {
    void navigate({ search: (previous) => toSearch(previous, changes), replace: true })
  }

  // The export takes the same filters the table is showing, so "export what I
  // am looking at" is the same set rather than a similar one. Paging is left
  // out: an export is the whole result, not the page.
  const exportQuery = new URLSearchParams(
    Object.entries(search)
      .filter(
        ([key, value]) =>
          value !== undefined && key !== 'page' && key !== 'per_page' && key !== 'view',
      )
      .map(([key, value]) => [key, String(value)]),
  ).toString()

  const rows = parts.data?.items ?? []
  const total = parts.data?.total ?? 0
  const page = filter.page ?? 1
  const perPage = filter.per_page ?? 50
  const lastPage = Math.max(1, Math.ceil(total / perPage))

  return (
    <Page
      title="Parts"
      description={
        parts.data
          ? `${total} part${total === 1 ? '' : 's'}${
              countActive(filter) > 0 || filter.q ? ' · filtered' : ''
            }`
          : 'Loading…'
      }
      actions={
        <>
          <Segmented
            label="View"
            value={search.view ?? 'table'}
            onChange={(view) =>
              void navigate({
                search: (previous) => ({
                  ...previous,
                  view: view === 'gallery' ? 'gallery' : undefined,
                }),
                replace: true,
              })
            }
            options={[
              { value: 'table', label: 'Table' },
              { value: 'gallery', label: 'Gallery' },
            ]}
          />
          <Button asChild variant="secondary">
            <a href={`/api/export/parts.csv${exportQuery ? `?${exportQuery}` : ''}`} download>
              <DownloadIcon />
              Export CSV
            </a>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button>
                <PlusIcon />
                New part
                <ChevronDownIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to="/parts/new">Blank</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/providers">From a distributor</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {filter.q && (
          <p className="text-muted-foreground text-sm">
            Searching for <span className="text-foreground font-medium">“{filter.q}”</span>.{' '}
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() => patch({ q: undefined, page: 1 })}
            >
              Clear
            </button>
          </p>
        )}

        <PartsFilterBar
          filter={filter}
          onChange={patch}
          onReset={() => void navigate({ search: { q: search.q }, replace: true })}
        />

        {parts.isPending ? (
          <LoadingRows label="Loading parts" rows={10} />
        ) : parts.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Could not load the parts</AlertTitle>
            <AlertDescription>
              {parts.error instanceof Error ? parts.error.message : 'Unknown error'}
            </AlertDescription>
          </Alert>
        ) : rows.length === 0 ? (
          <div className="border border-dashed border-neutral-400 p-10 text-center text-sm text-neutral-600">
            Nothing matches.
          </div>
        ) : (
          <>
            {search.view === 'gallery' ? (
              <PartsGallery rows={rows} />
            ) : (
              <PartsTable
                rows={rows}
                filter={filter}
                baseCurrency={settings.data?.base_currency ?? 'EUR'}
                selected={selected}
                onSelectedChange={setSelected}
                onSort={(field) =>
                  patch({
                    sort: field,
                    order: filter.sort === field && filter.order !== 'desc' ? 'desc' : undefined,
                    page: 1,
                  })
                }
              />
            )}

            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="text-muted-foreground">
                Rows {Math.min(total, (page - 1) * perPage + 1)}–{Math.min(total, page * perPage)}{' '}
                of {total}
              </span>
              {selected.size > 0 && (
                <>
                  <span className="text-muted-foreground">· {selected.size} selected</span>
                  <Button variant="outline" size="sm" onClick={() => setPrinting(true)}>
                    <PrinterIcon className="size-4" /> Print labels
                  </Button>
                </>
              )}
              <div className="ml-auto flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => patch({ page: page - 1 })}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= lastPage}
                  onClick={() => patch({ page: page + 1 })}
                >
                  Next
                </Button>
              </div>
            </div>

            <PrintLabelDialog
              open={printing}
              onOpenChange={setPrinting}
              targets={[...selected].map((id) => ({ kind: 'part' as const, id }))}
            />
          </>
        )}
      </div>
    </Page>
  )
}
